import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import HeroSlider from '../components/HeroSlider';
import LocationsMap from '../components/LocationsMap';
import { useLanguage } from '../i18n/LanguageContext';

// Advanced Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;



const HomeContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
`;



// Vicat Style Stats Section
const StatsSection = styled.section`
  width: 100%;
  padding: 100px 5%;
  background: #ffffff;
  text-align: center;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 60px 5%;
  }
`;

const StatsTitle = styled.h2`
  font-size: 4.5rem;
  
  color: #2c2c2c;
  margin-bottom: 60px;
  letter-spacing: -2px;
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 40px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 100px;
  max-width: 1200px;
  margin: 0 auto;
  
  &.animate-in {
    .stat-item:nth-child(1) { animation: ${scaleIn} 0.6s ease-out 0.2s both; }
    .stat-item:nth-child(2) { animation: ${scaleIn} 0.6s ease-out 0.4s both; }
    .stat-item:nth-child(3) { animation: ${scaleIn} 0.6s ease-out 0.6s both; }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 15px;
  }
`;

const StatItem = styled.div`
  text-align: center;
  transition: all 0.4s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
  
  &:hover .icon-circle {
    background: linear-gradient(135deg, #2B4B8C, #3B82C4);
    transform: scale(1.1);
  }
  
  .icon-circle {
    width: 120px;
    height: 120px;
    background: #2B4B8C;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 30px;
    transition: all 0.4s ease;
    box-shadow: 0 10px 30px rgba(43, 75, 140, 0.2);
    
    .icon {
      font-size: 3rem;
      color: white;
    }
  }
  
  h3 {
    font-size: 1.8rem;
    font-weight: 400;
    color: #2B4B8C;
    margin-bottom: 20px;
    letter-spacing: -0.5px;
  }
  
  .number {
    font-size: 4rem;
    
    color: #2c2c2c;
    margin-bottom: 15px;
    letter-spacing: -2px;
    display: block;
  }
  
  .description {
    font-size: 1.1rem;
    color: #6c757d;
    
    line-height: 1.6;
  }
  
  @media (max-width: 768px) {
    .icon-circle {
      width: 50px;
      height: 50px;
      margin-bottom: 10px;
      .icon {
        font-size: 1.2rem;
      }
    }
    
    h3 {
      font-size: 0.9rem;
      margin-bottom: 5px;
      letter-spacing: -0.2px;
    }
    
    .number {
      font-size: 1.8rem;
      margin-bottom: 2px;
    }
    
    .description {
      font-size: 0.75rem;
      line-height: 1.2;
    }
  }
`;

// Vicat Style Applications Section
const ApplicationsSection = styled.section`
  width: 100%;
  padding: 100px 5%;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  position: relative;
  
  @media (max-width: 768px) {
    padding: 60px 5%;
  }
`;

const ApplicationsTitle = styled.h2`
  font-size: 4.5rem;
  
  color: #2c2c2c;
  margin-bottom: 60px;
  text-align: center;
  letter-spacing: -2px;
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 40px;
  }
`;

const ApplicationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  max-width: 1400px;
  margin: 0 auto;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  
  &.animate-in {
    .app-card:nth-child(1) { animation: ${fadeInUp} 0.6s ease-out 0.1s both; }
    .app-card:nth-child(2) { animation: ${fadeInUp} 0.6s ease-out 0.3s both; }
    .app-card:nth-child(3) { animation: ${fadeInUp} 0.6s ease-out 0.5s both; }
    .app-card:nth-child(4) { animation: ${fadeInUp} 0.6s ease-out 0.7s both; }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ApplicationCard = styled.div`
  height: 400px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s ease;
  
  @media (max-width: 768px) {
    height: 250px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => `url(${props.bgImage}) no-repeat center center`};
    background-size: cover;
    transition: transform 0.4s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(43, 75, 140, 0.8), rgba(59, 130, 196, 0.6));
    transition: opacity 0.4s ease;
  }
  
  &:hover::before {
    transform: scale(1.1);
  }
  
  &:hover::after {
    opacity: 0.9;
  }
  
  &:hover {
    transform: translateY(-5px);
  }
  
  &:hover .card-content h3 {
    transform: translateY(-10px);
  }
  
  .card-content {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 40px 30px;
    z-index: 2;
    
    @media (max-width: 768px) {
      padding: 25px 20px;
    }
    
    h3 {
      color: white;
      font-size: 1.8rem;
      font-weight: 400;
      letter-spacing: -0.5px;
      transition: all 0.4s ease;
      text-transform: uppercase;
      line-height: 1.3;
      
      @media (max-width: 768px) {
        font-size: 1.3rem;
      }
    }
  }
`;

// Enhanced About Section
const AboutSection = styled.section`
  width: 100%;
  padding: 100px 5%;
  background: #ffffff;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 60px 5%;
  }
`;

const AboutContentWrapper = styled.div`
  max-width: 900px;
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
`;

const SectionTitle = styled.h2`
  font-size: 4.5rem;
  
  color: #2c2c2c;
  margin-bottom: 30px;
  letter-spacing: -2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, #2B4B8C, #3B82C4);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
    letter-spacing: -1px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.3rem;
  color: #6c757d;
  margin-bottom: 60px;
  max-width: 700px;
  
  line-height: 1.7;
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const TextBlock = styled.div`
  h3 {
    font-size: 2.8rem;
    
    color: #2c2c2c;
    margin-bottom: 35px;
    letter-spacing: -1.5px;
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      left: -30px;
      top: 0;
      width: 4px;
      height: 100%;
      background: linear-gradient(180deg, #2B4B8C, #3B82C4);
      border-radius: 2px;
    }
  }
  
  p {
    font-size: 1.15rem;
    line-height: 1.8;
    color: #6c757d;
    margin-bottom: 30px;
    
    transition: color 0.3s ease;
    
    &:hover {
      color: #495057;
    }
  }
`;



// Enhanced Features Section
const FeaturesSection = styled.section`
  width: 100%;
  padding: 100px 5%;
  background: linear-gradient(135deg, #2B4B8C 0%, #3B82C4 100%);
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 60px 5%;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.03) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const FeaturesTitle = styled.h2`
  font-size: 4.5rem;
  
  color: #ffffff;
  margin-bottom: 30px;
  text-align: center;
  letter-spacing: -2px;
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const FeaturesSubtitle = styled.p`
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 60px;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  
  line-height: 1.7;
  text-align: center;
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 60px;
  
  &.animate-in {
    .feature-card:nth-child(1) { animation: ${scaleIn} 0.6s ease-out 0.1s both; }
    .feature-card:nth-child(2) { animation: ${scaleIn} 0.6s ease-out 0.3s both; }
    .feature-card:nth-child(3) { animation: ${scaleIn} 0.6s ease-out 0.5s both; }
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const FeatureCard = styled.div`
  text-align: center;
  padding: 40px 30px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.4s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-10px);
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
  }
  
  &:hover .number {
    color: #ffffff;
    transform: scale(1.1);
  }
  
  &:hover h3 {
    color: #ffffff;
  }
  
  .number {
    font-size: 4rem;
    
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 40px;
    letter-spacing: -2px;
    transition: all 0.4s ease;
    display: inline-block;
  }
  
  h3 {
    font-size: 1.8rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 25px;
    transition: all 0.3s ease;
    letter-spacing: -0.5px;
  }
  
  p {
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.8;
    
    transition: color 0.3s ease;
  }
  
  &:hover p {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const Home = () => {
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <HomeContainer>
      <HeroSlider />

      <StatsSection 
        id="stats-section" 
        data-animate
      >
        <StatsTitle className={animatedSections.has('stats-section') ? 'animate-in' : ''}>
          {t('home.statsTitle')}
        </StatsTitle>
        
        <StatsGrid className={animatedSections.has('stats-section') ? 'animate-in' : ''}>
          <StatItem className="stat-item">
            <div className="icon-circle">
              <span className="icon">🏭</span>
            </div>
            <h3>{t('home.cement')}</h3>
            <span className="number">4</span>
            <div className="description">{t('home.cementFactory')}</div>
          </StatItem>
          
          <StatItem className="stat-item">
            <div className="icon-circle">
              <span className="icon">🚛</span>
            </div>
            <h3>{t('home.concrete')}</h3>
            <span className="number">11</span>
            <div className="description">{t('home.concretePlant')}</div>
          </StatItem>
          
          <StatItem className="stat-item">
            <div className="icon-circle">
              <span className="icon">⚡</span>
            </div>
            <h3>{t("header.subsidiaries")}</h3>
            <span className="number">3</span>
            <div className="description">{t("home.activeCompanies")}</div>
          </StatItem>
        </StatsGrid>
      </StatsSection>

      <LocationsMap />

      <ApplicationsSection 
        id="applications-section" 
        data-animate
      >
        <ApplicationsTitle className={animatedSections.has('applications-section') ? 'animate-in' : ''}>
          {t('home.applicationsTitle')}
        </ApplicationsTitle>
        
        <ApplicationsGrid className={animatedSections.has('applications-section') ? 'animate-in' : ''}>
          <ApplicationCard 
            className="app-card"
            bgImage="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
          >
            <div className="card-content">
              <h3>{t('home.buildings')}</h3>
            </div>
          </ApplicationCard>
          
          <ApplicationCard 
            className="app-card"
            bgImage="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
          >
            <div className="card-content">
              <h3>{t('home.residentialProjects')}</h3>
            </div>
          </ApplicationCard>
          
          <ApplicationCard 
            className="app-card"
            bgImage="https://images.unsplash.com/photo-1449824913935-59a10b8d2000?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
          >
            <div className="card-content">
              <h3>{t('home.roadInfra')}</h3>
            </div>
          </ApplicationCard>
          
          <ApplicationCard 
            className="app-card"
            bgImage="https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
          >
            <div className="card-content">
              <h3>{t('home.industrial')}</h3>
            </div>
          </ApplicationCard>
        </ApplicationsGrid>
      </ApplicationsSection>

      <AboutSection 
        id="about-section" 
        data-animate
      >
        <SectionTitle className={animatedSections.has('about-section') ? 'animate-in' : ''}>
          {t('home.aboutTitle')}
        </SectionTitle>
        <SectionSubtitle className={animatedSections.has('about-section') ? 'animate-in' : ''}>
          {t('home.aboutSubtitle')}
        </SectionSubtitle>
        
        <AboutContentWrapper 
          className={animatedSections.has('about-section') ? 'animate-in' : ''}
        >
          <TextBlock className="text-block">
            <h3>{t('home.modernProduction')}</h3>
            <p>
              {t('home.modernProductionP1')}
            </p>
            <p>
              {t('home.modernProductionP2')}
            </p>
          </TextBlock>
        </AboutContentWrapper>
      </AboutSection>

      <FeaturesSection 
        id="features-section" 
        data-animate
      >
        <FeaturesTitle className={animatedSections.has('features-section') ? 'animate-in' : ''}>
          {t('home.whyDmr')}
        </FeaturesTitle>
        <FeaturesSubtitle className={animatedSections.has('features-section') ? 'animate-in' : ''}>
          {t('home.whyDmrSubtitle')}
        </FeaturesSubtitle>
        
        <FeaturesGrid className={animatedSections.has('features-section') ? 'animate-in' : ''}>
          <FeatureCard className="feature-card">
            <div className="number">01</div>
            <h3>{t('home.qualityProduction')}</h3>
            <p>
              {t('home.qualityProductionDesc')}
            </p>
          </FeatureCard>
          
          <FeatureCard className="feature-card">
            <div className="number">02</div>
            <h3>{t('home.reliableDelivery')}</h3>
            <p>
              {t('home.reliableDeliveryDesc')}
            </p>
          </FeatureCard>
          
          <FeatureCard className="feature-card">
            <div className="number">03</div>
            <h3>{t('home.expertTeam')}</h3>
            <p>
              {t('home.expertTeamDesc')}
            </p>
          </FeatureCard>
        </FeaturesGrid>
      </FeaturesSection>
    </HomeContainer>
  );
};

export default Home;